import { Platform } from 'react-native';

import type { NativeAppClientInfo } from '../types/nativeAppClientInfo';

import { getLaunchParam } from './launchParams';

/**
 * Native-app / iframe embed flags derived from the launch params — mirrors
 * Sparta's `getNativeAppClientInfo` (sparta/src/utils/getNativeAppClientInfo.ts).
 *
 * The host that embeds the onboarding web bundle appends
 * `?vida_client=…&vida_client_version=…`:
 *   - `ios` / `android`  → loaded in a native-app webview (`isLoadedInNativeApp`)
 *   - `react-native-web` → loaded in an iframe, e.g. inside Sparta (`isLoadedInIframe`)
 *
 * Both flags additionally require `Platform.OS === 'web'`: they describe a *web*
 * bundle hosted inside a native shell / iframe, so a genuine native build (the
 * VidaReactNative npm-package case — `Platform.OS` ios/android, no `vida_client`
 * param) reads `false`. `vida_client` itself stays a pure mirror of the launch
 * param; the runtime platform is a separate dimension (PostHog's `platform`).
 *
 * Reads from the memoized launch params, so the result is stable even after the
 * URL params are dropped; memoized here too so repeated reads are cheap.
 */
let cached: NativeAppClientInfo | null = null;

export const getNativeAppClientInfo = (): NativeAppClientInfo => {
  if (cached) return cached;
  const vidaClient = getLaunchParam('vida_client')?.toLowerCase();
  const isWeb = Platform.OS === 'web';
  cached = {
    vidaClient,
    vidaClientVersion: getLaunchParam('vida_client_version'),
    isLoadedInNativeApp: isWeb && (vidaClient === 'ios' || vidaClient === 'android'),
    isLoadedInIframe: isWeb && vidaClient === 'react-native-web',
  };
  return cached;
};
